import { useEffect, useId, useState } from 'react';
import { destinationKey, validateParticipation, type RouteVisit, type TripParticipation } from '../../functions/src/travellerRoutePolicy.mjs';
import { changeTripParticipation } from '../services/tripMembership';
import { getPendingInvitations, type PendingTripInvitation } from '../services/userLookup';
import type { TripConfig } from '../types';
import { shortDateLabel } from '../utils/tripUtils';
import { FIELD, fieldBorder } from './UI';
import { Modal } from './Modal';
import { ItineraryJunctionLine } from './ItineraryJunctionLine';
import { junctionDate } from '../utils/itineraryBranches';
import { userMessage } from '../utils/userMessage';

/** Date exceptions sit within the existing card, without creating another route. */
export function TripParticipationNotice({ trip, visitId, date }: { trip: TripConfig; visitId: string; date?: string }) {
  const groups = new Map<string, { label: string; date: string; ids: string[] }>();
  for (const id of trip.memberIds ?? []) {
    const dates = trip.participation?.[id];
    if (!dates) continue;
    for (const edge of ['start', 'end'] as const) {
      const at = dates[edge === 'start' ? 'startVisitId' : 'endVisitId'];
      const when = dates[edge === 'start' ? 'startDate' : 'endDate'];
      if (at !== visitId || date && date !== when) continue;
      const key = `${edge}:${when}`;
      const group = groups.get(key) ?? { label: edge === 'start' ? 'Joining' : 'Leaving', date: when, ids: [] };
      group.ids.push(id); groups.set(key, group);
    }
  }
  return <>{[...groups.entries()].sort(([, a], [, b]) => a.date.localeCompare(b.date)).map(([key, group]) =>
    <ItineraryJunctionLine key={key} inset={!date} rejoining={group.label === 'Joining'}
      people={trip.travellers.filter(t => group.ids.includes(t.id))}
      label={`${group.label} on ${junctionDate(group.date)}`} />)}</>;
}

export function participationError(destinations: RouteVisit[], dates: TripParticipation | null): string {
  try { validateParticipation(destinations, dates); return ''; }
  catch (error) { return (error as Error).message; }
}

/** One compact choice, reused for inviting, joining and later changes. */
export function TripParticipationFields({ destinations, value, onChange, disabled = false }: {
  destinations: RouteVisit[]; value: TripParticipation | null; onChange: (value: TripParticipation | null) => void; disabled?: boolean;
}) {
  const name = useId();
  const stops = [...destinations].sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  return <fieldset disabled={disabled} className="space-y-4">
    <legend className="text-sm text-app-sub mb-2">Joining</legend>
    <div className="flex gap-4 text-sm text-app-text">
      <label className="flex items-center gap-2 min-h-11"><input type="radio" name={name} checked={!value} onChange={() => onChange(null)} className="accent-gold" />Whole trip</label>
      {!!stops.length && <label className="flex items-center gap-2 min-h-11"><input type="radio" name={name} checked={!!value} onChange={() => onChange({
        startVisitId: destinationKey(stops[0]), endVisitId: destinationKey(stops.at(-1)!), startDate: stops[0].arrivalDate, endDate: stops.at(-1)!.departureDate,
      })} className="accent-gold" />Part of the trip</label>}
    </div>
    {value && (['start', 'end'] as const).map(edge => {
      const key = edge === 'start' ? 'startVisitId' : 'endVisitId';
      const dateKey = edge === 'start' ? 'startDate' : 'endDate';
      const stop = stops.find(v => destinationKey(v) === value[key]);
      return <div key={edge} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1 text-sm text-app-sub"><span>{edge === 'start' ? 'Join in' : 'Leave from'}</span>
          <select value={value[key]} onChange={event => {
            const selected = stops.find(v => destinationKey(v) === event.target.value)!;
            onChange({ ...value, [key]: event.target.value, [dateKey]: edge === 'start' ? selected.arrivalDate : selected.departureDate });
          }} className={`${FIELD} ${fieldBorder(false)}`}>
            {!stop && <option value={value[key]}>Choose a destination</option>}
            {stops.map(v => <option key={destinationKey(v)} value={destinationKey(v)}>{v.name} · {shortDateLabel(v.arrivalDate)}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-sm text-app-sub"><span>{edge === 'start' ? 'Joining date' : 'Leaving date'}</span>
          <input type="date" value={value[dateKey]} min={stop?.arrivalDate} max={stop?.departureDate}
            onChange={event => onChange({ ...value, [dateKey]: event.target.value })} className={`${FIELD} ${fieldBorder(false)}`} />
        </label>
      </div>;
    })}
  </fieldset>;
}

const saveButton = 'w-full min-h-11 px-4 py-3 rounded-xl bg-gold text-white font-semibold disabled:opacity-40';

export function MyTripDates({ trip, userId, onClose, onSaved }: {
  trip: TripConfig; userId: string; onClose: () => void; onSaved: (dates: TripParticipation | null) => void;
}) {
  const [before] = useState(trip.participation?.[userId] ?? null);
  const [dates, setDates] = useState(before);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return <Modal title="My trip dates" onClose={() => { if (!busy) onClose(); }}>
    <div className="space-y-5">
      <TripParticipationFields destinations={trip.destinations} value={dates} onChange={value => { setDates(value); setError(''); }} disabled={busy} />
      {dates && <p className="text-sm text-app-sub">Bookings and expenses stay unchanged.</p>}
      {!!error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
      <button type="button" className={saveButton} disabled={busy} onClick={async () => {
        const invalid = participationError(trip.destinations, dates);
        if (invalid) { setError(invalid); return; }
        setBusy(true);
        try { const saved = await changeTripParticipation(trip.id, userId, before, dates); onSaved(saved); onClose(); }
        catch (error) { setError(userMessage(error, 'Couldn’t save these dates. Reopen and try again.')); }
        finally { setBusy(false); }
      }}>{busy ? 'Saving…' : 'Save dates'}</button>
    </div>
  </Modal>;
}

export function JoinTripDates({ tripId, onClose, onJoin }: {
  tripId: string; onClose: () => void; onJoin: (dates: TripParticipation | null) => Promise<void>;
}) {
  const [invitation, setInvitation] = useState<PendingTripInvitation | null>(null);
  const [dates, setDates] = useState<TripParticipation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    getPendingInvitations(tripId).then(invitations => {
      if (!live) return;
      const invite = invitations.find(i => i.tripId === tripId);
      if (!invite) { setError('This invitation is no longer available.'); return; }
      setInvitation(invite); setDates(invite.participation ?? null);
    }).catch(() => { if (live) setError('Couldn’t load the invitation. Close and try again.'); });
    return () => { live = false; };
  }, [tripId]);
  return <Modal title={invitation ? `Join ${invitation.title}` : 'Join trip'} onClose={() => { if (!busy) onClose(); }}>
    <div className="space-y-5">
      {invitation ? <TripParticipationFields destinations={invitation.destinations ?? []} value={dates} onChange={value => { setDates(value); setError(''); }} disabled={busy} />
        : !error && <p role="status" className="text-sm text-app-sub">Loading invitation…</p>}
      {!!error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
      <button type="button" className={saveButton} disabled={!invitation || busy} onClick={async () => {
        const invalid = participationError(invitation?.destinations ?? [], dates);
        if (invalid) { setError(invalid); return; }
        setBusy(true);
        try { await onJoin(dates); }
        catch (error) { setError(userMessage(error, 'Couldn’t join. Try again.')); }
        finally { setBusy(false); }
      }}>{busy ? 'Joining…' : 'Join trip'}</button>
    </div>
  </Modal>;
}
